import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';

import { selectRTFO } from '../../reducers/rtfoSlice';
import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { useTheme } from '@mui/styles';
import { rtfoConversionFactors } from './rtfoConversionFactors';

export const RTFOFracfeedSimulatorChart = ({
  gridView,
  showSpikeLines,
  showLegend
}) => {
  const theme = useTheme();
  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;

  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
  const stageData = calculateFracfeed(currentRTFO, currentStage?.coldboreData);
  const units = currentRTFO?.settings?.units || 'metric';

  const data = useMemo(() => {
    if (!stageData || !stageData?.length > 0) return [];

    return getChartData();

    function getChartData() {
      try {
        const X = [];
        const YSurfPressure = [];
        const YBHPressure = [];
        const YPipeFriction = [];
        const YPerfFriction = [];
        const YNWBTortuosity = [];
        const YRate = [];
        
        stageData?.forEach((d, i) => {
          X.push(d.timestamp);
          YSurfPressure.push((d.estPressure || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
          YBHPressure.push((d.fracBHPressure || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
          // YSurfPressure.push(d.rate > 0.1 ? (d.estPressure || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1) : 0);
          // YBHPressure.push(d.rate > 0.1 ? (d.estPressure > 0 && d.estPressure < d.fracBHPressure ? d.estPressure : (d.fracBHPressure || 0)) * (units === 'field' ? rtfoConversionFactors.pressure : 1) : 0);
          YPipeFriction.push((d.pipeFriction || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
          YPerfFriction.push((d.perfFriction || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
          YNWBTortuosity.push((d.nwbTortuosity || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
          YRate.push((d.rate || 0) * (units === 'field' ? rtfoConversionFactors.rate : 1));
        });

        // add traces
        const surfPressureTrace = {
          x: X,
          y: YSurfPressure,
          mode: 'line',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y1',
          fill: "tonexty",
          name: `Surf. Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
          line: {
            width: 2,
            color: chartColors[2]
          },
          hoverinfo: !showSpikeLines && 'none',
          connectgaps: true,
          hoverformat: '.2f'
        };
        const bhPressureTrace = {
          x: X,
          y: YBHPressure,
          mode: 'line',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y1',
          fill: 'tonexty',
          name: `Frac. Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
          line: {
            width: 2,
            color: chartColors[3]
          },
          hoverinfo: !showSpikeLines && 'none',
          connectgaps: true,
          hoverformat: '.2f'
        };
        const pipeFrictionTrace = {
          x: X,
          y: YPipeFriction,
          mode: 'line',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y1',
          fill: 'tonexty',
          name: `Pipe Friction (${units === 'field' ? 'psi' : 'MPa'})`,
          line: {
            width: 2,
            color: chartColors[4]
          },
          hoverinfo: !showSpikeLines && 'none',
          connectgaps: true,
          hoverformat: '.2f'
        };
        const perfFrictionTrace = {
          x: X,
          y: YPerfFriction,
          mode: 'line',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y1',
          fill: 'tonexty',
          name: `Perf. Friction (${units === 'field' ? 'psi' : 'MPa'})`,
          line: {
            width: 2,
            color: chartColors[5]
          },
          hoverinfo: !showSpikeLines && 'none',
          connectgaps: true,
          hoverformat: '.2f'
        };
        const nwbTortuosityTrace = {
          x: X,
          y: YNWBTortuosity,
          mode: 'line',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y1',
          fill: 'tozeroy',
          name: `NWB Tortuosity (${units === 'field' ? 'psi' : 'MPa'})`,
          line: {
            width: 2,
            color: chartColors[6]
          },
          hoverinfo: !showSpikeLines && 'none',
          connectgaps: true,
          hoverformat: '.2f'
        };
        const rateTrace = {
          x: X,
          y: YRate,
          mode: 'line',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y2',
          name: `Slurry Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
          line: {
            width: 2,
            color: chartColors[7]
          },
          hoverinfo: !showSpikeLines && 'none',
          connectgaps: true,
          hoverformat: '.2f'
        };

        let traces = [
          nwbTortuosityTrace,
          perfFrictionTrace,
          pipeFrictionTrace,
          bhPressureTrace,
          surfPressureTrace,
          rateTrace
        ];

        return traces;
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [stageData, units, showSpikeLines]);

  const layout = {
    yaxis: {
      // Pressure y axis
      linecolor: chartColors[2],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[2],
      title: {
        text: `Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
        font: {
          size: 12
        }
      },
      anchor: 'x',
      range: units === 'field' ? [0, 15000] : [0, 100],
      tick0: 0,
      dtick: units === 'field' ? 3000 : 20,
      rangemode: 'nonnegative',
      side: 'left',
      hoverformat: '.2f'
    },
    yaxis2: {
      // Rate y axis
      linecolor: chartColors[7],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[7],
      title: {
        text: `Slurry Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
        font: {
          size: 12
        }
      },
      anchor: 'x',
      range: units === 'field' ? [0, 150] : [0, 25],
      tick0: 0,
      dtick: units === 'field' ? 30 : 5,
      rangemode: 'nonnegative',
      side: 'right',
      overlaying: 'y',
      hoverformat: '.2f'
    },
    xaxis: {
      // Date x axis
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: 'Date',
        font: {
          size: 12
        }
      },
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: true,
      spikethickness: 2,
      spikedash: 'dot',
      spikecolor: '#999999',
      spikemode: 'across',
      type: 'date'
    },
    hovermode: 'x',
    hoverdistance: 20, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: showLegend ? true : false,
    legend: {
      orientation: 'h',
      yanchor: 'top',
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: gridView ? -0.15 : -0.1
    },
    annotations: [
      {
        text: 'Fracfeed Simulator Chart',
        font: {
          size: 14,
          color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: gridView ? 1.15 : 1.075, //position in y domain
        xref: 'paper',
        yref: 'paper'
      },
      {
        text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber || 'No stage selected'}`,
        font: {
          size: 10,
          color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: gridView ? 1.09 : 1.035, //position in y domain
        xref: 'paper',
        yref: 'paper'
      }
    ],
    shapes: [
    ],
    uirevision: 'true',
    margin: { t: 60, l: 60, r: 60, b: 60 },
    images: [
      {
        x: gridView ? 0.05 : 0.035,
        y: gridView ? 1.05 : 1.025,
        sizex: gridView ? 0.1 : 0.05,
        sizey: 0.1,
        source: `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: 'page',
        yref: 'y1 domain',
        xanchor: 'right',
        yanchor: 'bottom',
        opacity: 0.75
      }
    ],
    plot_bgcolor: 'transparent',
    paper_bgcolor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: 'RTFO-' + new Date().toISOString().split('T')[0],
      // filename: currentFBA?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{ width: '100%', height: '100%' }}
    />
  );


  // FUNCTIONS

  
};

export function calculateFracfeed(currentRTFO, stageData) {
  try {
    // const stageDepth = stage
    const stageName = currentRTFO?.currentStageName;
    const stage = currentRTFO?.stageData?.[stageName];
    const wellName = stage?.wellName;
    const stageNumber = String(Number(String(stage?.stageNumber)?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim()));;
  
    // determine stage MD and TVD
    const stageClusters = currentRTFO?.perfSchedule?.wells?.[wellName?.toLowerCase()]?.stages?.[stageNumber]?.clusters;
    const clusterNames = Object.keys(stageClusters || {});
    const depths = clusterNames?.map(cluster => stageClusters[cluster]?.shotDepth);
    const md = depths?.reduce((a, b) => a + b, 0) / depths?.length;
    const crossOverDepth = 0; // ADD THIS LATER!!!
    const tvd = currentRTFO?.surveys?.wells?.[wellName?.toLowerCase()]?.surveyData?.find((data) => data.measuredDepth >= md)?.trueVerticalDepth;
  
    // determine perf information
    const numPerfs = clusterNames?.map(cluster => stageClusters[cluster]?.numShots)?.reduce((a, b) => a + b, 0);
    const entryHoleDiameter = clusterNames?.map(cluster => stageClusters[cluster]?.entryHoleDiameter)?.reduce((a, b) => a + b, 0) / clusterNames?.length;
    const preSandCd = stage?.stepdownParams?.preSandCd || 0.7;
    const Cd = stage?.stepdownParams?.Cd || 0.9;
  
    // fluid and proppant properties
    const fluidDensity = 1000; // kg/m3
    const propDensity = 2650; // kg/m3
  
    // erosion model
    const critPropPerPerf = 0.2; // tonnes/perf
    const erosionRate = 0.0083; // in/tonne
    const critPropMass = numPerfs * critPropPerPerf; // tonnes
  
    // near wellbore tortuosity
    const kNwb = 2.15;
    const bNwb = 0.65;
    // const kNwb = stage?.stepdownParams?.kNwb || 2.15;
    // const bNwb = stage?.stepdownParams?.bNwb || 0.65;
  
    // pipe friction model
    const fcCasing1 = currentRTFO?.settings?.wells?.[wellName?.toLowerCase()]?.casing1?.fc;
    const plExpCasing1 = currentRTFO?.settings?.wells?.[wellName?.toLowerCase()]?.casing1?.plExp;
    const fcCasing2 = currentRTFO?.settings?.wells?.[wellName?.toLowerCase()]?.casing2?.fc;
    const plExpCasing2 = currentRTFO?.settings?.wells?.[wellName?.toLowerCase()]?.casing2?.plExp;

    // isip (use prev stage)
    const wellStages = currentRTFO?.stages?.filter(s => s?.includes(wellName));
    const currentStageIndex = wellStages?.indexOf(stageName);
    const prevStageName = wellStages?.[Math.max(currentStageIndex - 1, 0)];

    // FETCH THIS!!!
    const prevStage = currentRTFO?.stageData?.[prevStageName];
    const prevStageISIP = prevStage?.metrics?.isip;
  
    const newStageData = [...stageData];

    const firstRateIndex = newStageData?.findIndex((d,i) => d.rate > 0.5 && newStageData[i+60]?.rate > 1);
    const lastRateIndex = newStageData?.findLastIndex((d,i) => d.rate > 0.5 && newStageData[i-60]?.rate > 1);

    // start iterative calculations
    for (let i = 0; i < newStageData?.length; i++) {
      const row = newStageData[i];
    
      const cleanVolume = i === 0 ? row.rate * row.time : row.rate * (row.time - newStageData[i-1].time);
      const propPumped = (cleanVolume * row.proppant / 1000) || 0;
      const cumPropPumped = i === 0 ? propPumped : (propPumped + newStageData[i-1].cumPropPumped);
      const slurryDensity = (row.proppant + fluidDensity) / (1 + row.proppant / propDensity);
      const perfDiameter = cumPropPumped <= critPropMass ? entryHoleDiameter : (propPumped * erosionRate / numPerfs + newStageData[i-1].perfDiameter);
      const CdCalc = cumPropPumped === 0 ? preSandCd : cumPropPumped <= critPropMass ? ((Cd - preSandCd) / critPropMass * propPumped + newStageData[i-1].CdCalc) : Cd;
      const fracBHPressure = prevStageISIP + slurryDensity * tvd * 9.806 / 1000 / 1000;
      const hydrPressure = slurryDensity * 9.806 * tvd / 1000 / 1000;
      const pipeFriction = (crossOverDepth * fcCasing2 * (row.rate ** plExpCasing2) + (md - crossOverDepth) * fcCasing1 * (row.rate ** plExpCasing1)) / 1000;
      const perfFriction = 0.2369 * ((row.rate * 6.3) ** 2) * (slurryDensity * 0.0083454) / ((CdCalc ** 2) * (numPerfs ** 2) * (perfDiameter ** 4)) * 0.00689476;
      const nwbTortuosity = kNwb * (row.rate ** bNwb);
      const estPressure = fracBHPressure + pipeFriction + perfFriction + nwbTortuosity - hydrPressure;
      const fracfeedCalc = row.rate === 0 ? 0 : estPressure / row.rate * 2 / 3;

      newStageData[i] = {
        ...row,
        cumPropPumped,
        perfDiameter,
        CdCalc,
        estPressure: (i < firstRateIndex || i > lastRateIndex) ? 0 : estPressure,
        fracBHPressure: (i < firstRateIndex || i > lastRateIndex) ? 0 : fracBHPressure,
        hydrPressure: (i < firstRateIndex || i > lastRateIndex) ? 0 : hydrPressure,
        pipeFriction: (i < firstRateIndex || i > lastRateIndex) ? 0 : pipeFriction,
        perfFriction: (i < firstRateIndex || i > lastRateIndex) ? 0 : perfFriction,
        nwbTortuosity: (i < firstRateIndex || i > lastRateIndex) ? 0 : nwbTortuosity,
        fracfeedCalc: (i < firstRateIndex || i > lastRateIndex) ? 0 : fracfeedCalc
      };

      // if (i < firstRateIndex || i > lastRateIndex) {
      //   newStageData[i] = {
      //     ...row,
      //     cumPropPumped: 0,
      //     perfDiameter: 0,
      //     CdCalc: 0,
      //     estPressure: 0,
      //     fracBHPressure: 0,
      //     hydrPressure: 0,
      //     pipeFriction: 0,
      //     perfFriction: 0,
      //     nwbTortuosity: 0,
      //     fracfeedCalc: 0
      //   };
      //   continue;
      // };

    };

    for (let i = 0; i < newStageData?.length; i++) {
      delete newStageData[i].cumPropPumped;
      delete newStageData[i].perfDiameter;
      delete newStageData[i].CdCalc;
    };

    return newStageData;
  } catch (error) {
    console.log(error);
    return stageData;
  };
};

export const RTFOFracfeedSimulatorChartMemo = React.memo(RTFOFracfeedSimulatorChart);
